import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as selector from "../../selectors/selectors"
import { Step, StepLabel, Stepper } from '@mui/material'
import { MediaDefaultIcon } from '../../icon'
import ButtonComponent from '../shared/button/button.component'
import ImportFile from './importFile'
import { CustomAttributeDefinitionModel } from '../../models/custom-attribute.model'
import MatchUploadedFileColoumns from './matchUploadedFileColoumns'
import ExcelContactData from './excelContactData'
import { Contact } from '../../models/conversations'
import { ImportedContact, ImportedContactModelWithErrors, InvalidImportRow, SelectExistingContactAction, SelectGroupAction } from '../../models/importContacts.model'
import { UtilityService } from '../../services/UtilityService'
import ExcelContactToGroup from './excelContactToGroup'
import { ColDef } from 'ag-grid-community'
import { LoadCustomAttributeDefinitions } from '../../actions'
import { ContactService } from '../../services/ContactService'
import setToast from '../../helpers/toaster.helper'
import { ToasterType } from '../shared/toaster/toaster.model'
import { useNavigate } from 'react-router'
import { AppRoutes } from '../routes/route.path'

const Normalize = (str: string) => (str || "").trim().toLowerCase();

const toBooleanValue = (value: string): string => {
    const input = Normalize(value);
    if (input === "true" || input === "yes") return "true";
    if (input === "false" || input === "no") return "false";
    return value;
};

// Default mapping
const defaultFieldMap: Record<string, string> = {
    firstname: "FirstName",
    lastname: "LastName",
    email: "Email",
    phonenumber: "PhoneNumber",
    mobile: "PhoneNumber",
    mobilenumber: "PhoneNumber"
};

const STANDARD_IMPORT_COLUMN_LABELS: Record<string, string> = {
    FirstName: "First Name",
    LastName: "Last Name",
    PhoneNumber: "Phone Number",
    Email: "Email",
};

const StepperImportNewComponent = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const IsDarkMode: boolean = useSelector(selector.isDarkMode)
    const CustomAttributeDefinitions: CustomAttributeDefinitionModel[] = useSelector(selector.customAttributeFields);
    const Contacts: Contact[] = useSelector(selector.contacts);

    const [activeStep, setActiveStep] = useState<number>(0)
    const stepContentScrollRef = useRef<HTMLDivElement>(null);
    const [uploadedFile, setUploadedFile] = useState<File>();
    const [jsonFileData, setjsonFileData] = useState<any[]>([]);
    const [fileColumnDefs, setfileColumnDefs] = useState<ColDef[]>([]);
    const [matchedHeaders, setMatchedHeaders] = useState<any[]>([]);
    const [unmatchedHeaders, setUnmatchedHeaders] = useState<string[]>([]);
    const [formattedContacts, setFormattedContacts] = useState<any[]>([]);
    const [headerMappingState, setHeaderMappingState] = useState<Record<string, any>>({});
    const [unmatchedSelection, setUnmatchedSelection] = useState<Record<string, string>>({});

    const [existingContacts, setExistingContacts] = useState<ImportedContact[]>([]);
    const [newValidContacts, setNewValidContacts] = useState<ImportedContact[]>([]);
    const [savedInvalidContacts, setSavedInvalidContacts] = useState<ImportedContact[]>([]);
    const [skippedInvalidContacts, setSkippedInvalidContacts] = useState<ImportedContactModelWithErrors[]>([]);
    const [invalidRows, setInvalidRows] = useState<InvalidImportRow[]>([]);
    const [invalidContactsForGroup, setInvalidContactsForGroup] = useState<ImportedContactModelWithErrors[]>([]);

    const [selectExistingContactAction, setSelectExistingContactAction] = useState<SelectExistingContactAction>(SelectExistingContactAction.Fill_Blank_Fields)
    const [selectGroupAction, setSelectGroupAction] = useState<SelectGroupAction>(SelectGroupAction.BOTH)
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([])
    const [includeIsGroupCheck, setIncludeIsGroupCheck] = useState<boolean>(false);
    const [isImportLoading, setIsImportLoading] = useState<boolean>(false)
    const [isFieldsUpdatingAgain, setIsFieldsUpdatingAgain] = useState<boolean>(false)

    const existingReduxContacts = useMemo(() => {
        const phoneSet = new Set<string>();
        Contacts.forEach((contact) => {
            const rawPhone = UtilityService.unmaskPhoneNumber(contact.PhoneNumber || "");
            if (rawPhone) {
                phoneSet.add(rawPhone);
            }
        });
        return phoneSet;
    }, [Contacts]);

    const customColumnsToShow = useMemo(
        () => CustomAttributeDefinitions.filter((def) => !def.IsDeleted),
        [CustomAttributeDefinitions]
    );

    const customDefinitionByReference = useMemo(() => {
        const map = new Map<string, CustomAttributeDefinitionModel>();
        customColumnsToShow.forEach((def) => map.set(def.Reference, def));
        return map;
    }, [customColumnsToShow]);

    const dropdownLookupByReference = useMemo(() => {
        const map = new Map<string, Map<string, string>>();
        customColumnsToShow.forEach((def) => {
            if (def.Type !== 4) return;
            const optionMap = new Map<string, string>();
            (def.OptionsDetail || []).forEach((option) => {
                optionMap.set(option.id, option.id);
                optionMap.set(Normalize(option.label), option.id);
            });
            map.set(def.Reference, optionMap);
        });
        return map;
    }, [customColumnsToShow]);

    //usememo
    const Steps: { label: string, component: React.ReactNode }[] =
        [{
            label: "Upload File", component: <ImportFile
                UploadedFile={uploadedFile}
                HandleUploadFileData={(file, jsonData) => { setUploadedFile(file); prepareGrid(jsonData) }}
                HandleClearFile={() => handleClearFile()}
            />
        },
        {
            label: "Match Columns", component: <MatchUploadedFileColoumns UploadedFile={uploadedFile} JsonFileData={jsonFileData} MatchedHeaders={matchedHeaders} UnmatchedHeaders={unmatchedHeaders} HeaderMappingState={headerMappingState} UnmatchedSelection={unmatchedSelection} UpdateUnMatchedSelection={(data) => setUnmatchedSelection(data)} UpdateHeaderMappingState={(data) => { setHeaderMappingState(data); setIsFieldsUpdatingAgain(true) }} UpdateMatchedHeaders={(data) => setMatchedHeaders(data)} FileColoumnDefs={fileColumnDefs} />
        },
        {
            label: "Review", component: <ExcelContactData Data={formattedContacts} UploadedFile={uploadedFile} SaveContactRow={(row, rowId) => saveContactRow(row, rowId)} SkipContactRow={(row, rowId) => skipContactRow(row, rowId)} NewContacts={newValidContacts} ExistingContacts={existingContacts} InValidRows={invalidRows} JsonFileData={jsonFileData} />
        },
        {
            label: "Import", component: <ExcelContactToGroup
                NewContacts={newValidContacts}
                ExistingContacts={existingContacts}
                SavedInvalidContacts={savedInvalidContacts}
                InvalidContacts={[...invalidContactsForGroup, ...skippedInvalidContacts]}
                UploadedFile={uploadedFile}
                IsChecked={includeIsGroupCheck}
                SelectedGroupIds={selectedGroupIds}
                SelectedExistingContactAction={(action) => setSelectExistingContactAction(action)}
                SelectedGroupAction={(action) => setSelectGroupAction(action)}
                UpdateSelectedGroups={(groups) => setSelectedGroupIds(groups)}
                UpdateIncludeIsGroupCheck={(isChecked) => setIncludeIsGroupCheck(isChecked)}
            />
        }
        ]

    const StepHeader: { Title: string, Description: string }[] = useMemo(() =>
        [{
            Title: "Upload your file",
            Description: "XLSX or CSV up to 25 MB. We'll detect columns, validate phone numbers, and find matches automatically."
        },
        {
            Title: "Match the columns",
            Description: "Tell us which spreadsheet columns belong to which contact fields. We auto-match what we recognize."
        },
        {
            Title: "Review what we found",
            Description: "Here's how your file breaks down. Fix or skip anything that doesn't look right."
        },
        {
            Title: "Confirm and import",
            Description: "A few last decisions, then we'll bring your contacts in."
        }
        ], []);

    const resetImportState = () => {
        setUploadedFile(undefined);
        setjsonFileData([]);
        setfileColumnDefs([]);
        setMatchedHeaders([]);
        setUnmatchedHeaders([]);
        setFormattedContacts([]);
        setHeaderMappingState({});
        setUnmatchedSelection({});
        setExistingContacts([]);
        setNewValidContacts([]);
        setSavedInvalidContacts([]);
        setSkippedInvalidContacts([]);
        setInvalidRows([]);
        setInvalidContactsForGroup([]);
        setSelectExistingContactAction(SelectExistingContactAction.Fill_Blank_Fields);
        setSelectGroupAction(SelectGroupAction.BOTH);
        setSelectedGroupIds([]);
        setIncludeIsGroupCheck(false);
    };

    const handleClearFile = () => {
        resetImportState();
    };

    const handleChangeFile = () => {
        resetImportState();
        setActiveStep(0);
    };

    const formatFileSize = (size: number) => {
        return size < 1024 * 1024 ? `${(size / 1024).toFixed(2)} KB` : `${(size / (1024 * 1024)).toFixed(2)} MB`;
    };

    //       ----------------------------    Step-1    ----------------------------------------------

    // Normalize
    const normalize = (str: string) =>
        str.toLowerCase().replace(/\./g, "").replace(/[^a-z0-9]/g, "");

    // Prepare Grid + Mapping
    const prepareGrid = (data: Record<string, string>[]) => {
        if (!data.length) return;
        const headers = Object.keys(data[0]);
        const matched: any[] = [];
        const unmatched: string[] = [];
        const headerMapping: Record<string, any> = {};

        const columns = headers.map((header) => {
            const normalizedHeader = normalize(header);

            const defaultKey = defaultFieldMap[normalizedHeader];
            const customDef = findCustomField(header);

            if (defaultKey) {
                matched.push({ excelHeader: header, mappedTo: defaultKey, type: "default" });

                headerMapping[header] = {
                    type: "default",
                    key: defaultKey
                };
            }
            else if (customDef) {
                matched.push({ excelHeader: header, mappedTo: customDef.Name, type: "custom" });

                headerMapping[header] = {
                    type: "custom",
                    customColumnDef: customDef
                };
            }
            else {
                unmatched.push(header);

                headerMapping[header] = {
                    type: "unmatched"
                };
            }

            return {
                headerName: header,
                field: header,
                sortable: true,
                filter: true,
                resizable: true,
                cellClass: "align-center d-flex",
                suppressMenu: true,
            };
        });

        setMatchedHeaders(matched);
        setUnmatchedHeaders(unmatched);
        setHeaderMappingState(headerMapping);
        setfileColumnDefs(columns);
        setjsonFileData(data);
    };

    // Find custom field
    const findCustomField = (header: string) => {
        const normalizedHeader = normalize(header);

        return CustomAttributeDefinitions.find((customColumnDef) => {
            const normalizedName = normalize(customColumnDef.Name);
            return (
                normalizedHeader === normalizedName
                // ||
                // normalizedName.includes(normalizedHeader) ||  
                // normalizedHeader.includes(normalizedName)
            );
        });
    };

    //            ------------------------    step-2    --------------------------------------
    // NEXT CLICK(contacts mapping) → format contacts
    const goToContactReviewStep = () => {
        const headers = Object.keys(jsonFileData[0] || {});
        const headerMapping = headerMappingState;
        const formatted = jsonFileData.map((row) => {
            let contact: ImportedContact = {
                FirstName: "",
                LastName: "",
                Email: "",
                PhoneNumber: "",
                CustomAttributes: []
            };

            headers.forEach((header) => {
                const value = (row[header] || "").toString().trim();
                if (!value) return;

                // skip
                if (unmatchedSelection[header] === "skip") return;

                const headerColumnMapping = headerMapping[header];
                if (!headerColumnMapping) return;

                if (headerColumnMapping.type === "default") {
                    contact[headerColumnMapping.key] = value;
                }
                else if (headerColumnMapping.type === "custom") {
                    contact.CustomAttributes.push({
                        id: headerColumnMapping.customColumnDef.Id,
                        reference: headerColumnMapping.customColumnDef.Reference,
                        name: headerColumnMapping.customColumnDef.Name,
                        value: headerColumnMapping.customColumnDef.Type === 4 ? headerColumnMapping.customColumnDef.OptionsDetail.find(x => normalize(x.label) === normalize(value))?.id ?? "" : value,
                        type: headerColumnMapping.customColumnDef.Type
                    });
                }
            });

            return contact;
        });

        setFormattedContacts(formatted);
        seprateData(formatted)
    };

    const seprateData = (data: ImportedContact[]) => {
        const existing: ImportedContact[] = [];
        const validNew: ImportedContact[] = [];
        const invalid: InvalidImportRow[] = [];

        const phoneCounts = new Map<string, number>();
        (data || []).forEach((contact) => {
            const phone = UtilityService.unmaskPhoneNumber(contact.PhoneNumber || "");
            if (!phone) return;
            phoneCounts.set(phone, (phoneCounts.get(phone) || 0) + 1);
        });

        (data || []).forEach((contact: ImportedContact, index: number) => {

            const customValues: Record<string, string> = {};
            for (const attr of (contact.CustomAttributes || [])) {
                customValues[attr.reference] = attr.value || "";
            }
            const row: InvalidImportRow = {
                RowId: `${index}`,
                FirstName: contact.FirstName || "",
                LastName: contact.LastName || "",
                Email: contact.Email || "",
                PhoneNumber: contact.PhoneNumber || "",
                customValues: customValues,
                Errors: {},
                CustomAttributes: contact.CustomAttributes
            };
            const Errors = validateRow(row);

            const normalizedPhone = UtilityService.unmaskPhoneNumber(contact.PhoneNumber || "");
            // file in duplicate contacts consider as invalid
            if (normalizedPhone && (phoneCounts.get(normalizedPhone) || 0) > 1) {
                Errors.PhoneNumber = Errors.PhoneNumber || "Duplicate phone number";
            }

            if (Object.keys(Errors)?.length > 0) {
                invalid.push({ ...row, Errors });
                return;
            }

            if (normalizedPhone && existingReduxContacts.has(normalizedPhone)) {
                existing.push(contact);
                return;
            }


            if (Object.keys(Errors).length === 0) {
                validNew.push(toImportedContact(row));
                return;
            }

            invalid.push({ ...row, Errors });
        });

        setExistingContacts(existing);
        setNewValidContacts(validNew);
        // setSavedInvalidContacts([]);
        // setSkippedInvalidContacts([]);
        setInvalidRows(invalid);
        setInvalidContactsForGroup([]);
    }
    const toImportedContact = useCallback((row: InvalidImportRow): ImportedContact => {
        const CustomAttributes = Object.entries(row.customValues || {})
            .map(([reference, rawValue]) => {
                const def = customDefinitionByReference.get(reference);
                if (!def) return null;
                const value = (rawValue || "").trim();
                if (!value) return null;
                const formattedValue = def.Type === 4
                    ? dropdownLookupByReference.get(def.Reference)?.get(value) || dropdownLookupByReference.get(def.Reference)?.get(Normalize(value)) || value
                    : def.Type === 3 ? toBooleanValue(value) : value;

                if (!formattedValue) return null;

                return {
                    id: def.Id || "",
                    reference: reference,
                    name: def.Name,
                    value: formattedValue,
                    type: def.Type
                };
            })
            .filter((x) => !!x);

        return {
            FirstName: (row.FirstName || "").trim(),
            LastName: (row.LastName || "").trim(),
            Email: (row.Email || "").trim(),
            PhoneNumber: UtilityService.unmaskPhoneNumber(row.PhoneNumber || ""),
            CustomAttributes,
        };
    }, [customDefinitionByReference, dropdownLookupByReference]);


    const validateStandardField = useCallback((field: "FirstName" | "LastName" | "PhoneNumber" | "Email", value: string) => {
        const trimmed = (value || "").trim();
        if (field === "FirstName") {
            if (!trimmed) {
                return "First Name is required.";
            }
            if (trimmed.length > 30) return "First Name should not exceed 30 characters.";
            return "";
        }
        if (field === "LastName") {
            if (trimmed && trimmed.length > 30) return "Last Name should not exceed 30 characters.";
            return "";
        }
        if (field === "PhoneNumber") {
            const phone = UtilityService.unmaskPhoneNumber(trimmed);
            if (!phone) return "Phone Number is required.";
            if (!UtilityService.validPhoneNumber(phone) || phone.length > 15) return "Invalid Phone Number.";
            return "";
        }
        if (field === "Email") {
            if (!trimmed) return "";
            if (!UtilityService.isValidEmail(trimmed)) return "Invalid Email.";
            return UtilityService.validateEmailLength(trimmed) || "";
        }
        return "";
    }, []);

    const validateCustomField = useCallback((reference: string, rawValue: string) => {
        const columnDef = customDefinitionByReference.get(reference);
        if (!columnDef) return "";
        const value = (rawValue || "").trim();
        if (columnDef.IsRequired && !value) {
            return `${columnDef.Name} is required.`;
        }
        if (!value) return "";
        if (columnDef.Type === 1 && Number.isNaN(Number(value))) {
            return `${columnDef.Name} must be a number.`;
        }
        if (columnDef.Type === 2) {
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return `${columnDef.Name} must be a valid date.`;
            }
        }
        if (columnDef.Type === 3) {
            const parsed = toBooleanValue(value);
            if (parsed !== "true" && parsed !== "false") {
                return `${columnDef.Name} must be true or false.`;
            }
        }
        if (columnDef.Type === 4) {
            const optionId = dropdownLookupByReference.get(reference)?.get(value) || dropdownLookupByReference.get(reference)?.get(Normalize(value)) || "";
            if (!optionId) {
                return `${columnDef.Name} has an invalid option.`;
            }
        }
        return "";
    }, [customDefinitionByReference, dropdownLookupByReference]);

    const validateRow = useCallback((row: InvalidImportRow): Record<string, string> => {
        const errors: Record<string, string> = {};
        const firstNameError = validateStandardField("FirstName", row.FirstName || "");
        const lastNameError = validateStandardField("LastName", row.LastName || "");
        const phoneError = validateStandardField("PhoneNumber", row.PhoneNumber || "");
        const emailError = validateStandardField("Email", row.Email || "");
        if (firstNameError) errors.FirstName = firstNameError;
        if (lastNameError) errors.LastName = lastNameError;
        if (phoneError) errors.PhoneNumber = phoneError;
        if (emailError) errors.Email = emailError;
        customColumnsToShow.forEach((def) => {
            const customError = validateCustomField(def.Reference, row.customValues[def.Reference] || "");
            if (customError) {
                errors[`custom:${def.Reference}`] = customError;
            }
        });

        return errors;
    }, [customColumnsToShow, validateCustomField, validateStandardField]);

    //          ------------------------------------    Step-3    -------------------------------------------

    const toImportedContactModelWithErrors = useCallback((row: InvalidImportRow): ImportedContactModelWithErrors => {
        const base = toImportedContact(row);
        const Errors: { columnName: string; errorMessage: string; }[] = Object.entries(row.Errors || {}).map(
            ([errorKey, errorMessage]) => {
                let columnName: string;
                if (errorKey.startsWith("custom:")) {
                    const ref = errorKey.slice("custom:".length);
                    columnName = customDefinitionByReference.get(ref)?.Name ?? ref;
                } else {
                    columnName = STANDARD_IMPORT_COLUMN_LABELS[errorKey] ?? errorKey;
                }
                return { columnName, errorMessage };
            }
        );
        return { ...base, Errors };
    },
        [customDefinitionByReference, toImportedContact]
    );

    const saveContactRow = (row: ImportedContact, rowId?: string) => {
        const normalizedPhone = UtilityService.unmaskPhoneNumber(row.PhoneNumber || "");
        if (normalizedPhone && existingReduxContacts.has(normalizedPhone)) {
            setExistingContacts((prev) => [...prev, row])
        } else {
            setNewValidContacts((prev) => [...prev, row]);
        }
        setSavedInvalidContacts((prev) => [...prev, row]);
        setInvalidRows((prev) => prev.filter((x) => x.RowId !== rowId));
    }

    const skipContactRow = useCallback((row: ImportedContactModelWithErrors, rowId?: string) => {
        setSkippedInvalidContacts((prev) => [...prev, row]);
        setInvalidRows((prev) => prev.filter((x) => x.RowId !== rowId));
    }, [toImportedContact]);

    //          ------------------------------------     Step-4     ------------------------------------------
    const handleImport = () => {
        setIsImportLoading(true)

        let data = {
            existingContacts: existingContacts,
            newContacts: newValidContacts,
            invalidContacts: [...invalidContactsForGroup, ...skippedInvalidContacts],
            selectedContactAction: selectExistingContactAction,
            selectedGroupAction: selectGroupAction,
            groups: includeIsGroupCheck ? [] : selectedGroupIds,
            fileName: uploadedFile.name
        }
        ContactService.importContactsFormfile(data).then((res) => {
            setIsImportLoading(false)
            navigate(AppRoutes.CONTACTS)
        }).catch((err) => {
            setIsImportLoading(false)
            setToast(err?.response?.data, ToasterType.DANGER)
        })
    }


    const renderNextStepButton = (activeStep: number) => {
        switch (activeStep) {
            case 0:
                return <ButtonComponent
                    ButtonLabel={'Next'}
                    Variant={!!uploadedFile ? 'primary-btn' : 'disabled-btn'}
                    ClassName=''
                    OnClick={() => setActiveStep(activeStep + 1)}
                />;
            case 1:
                return <ButtonComponent
                    ButtonLabel={'Continue to review →'}
                    Variant={'primary-btn'}
                    ClassName=''
                    OnClick={() => {
                        setActiveStep(activeStep + 1)
                        setIsFieldsUpdatingAgain(false)
                        if (((!!existingContacts?.length || !!invalidRows?.length || !!newValidContacts?.length) && !isFieldsUpdatingAgain)) {
                            return
                        }
                        goToContactReviewStep()
                    }}
                />;
            case 2:
                return <ButtonComponent
                    ButtonLabel={`Looks Good - ${existingContacts?.length + newValidContacts?.length} Contacts`}
                    Variant={(existingContacts?.length + newValidContacts?.length) > 0 ? 'primary-btn' : 'disabled-btn'}
                    ClassName=''
                    OnClick={() => {
                        setActiveStep(activeStep + 1)
                        setInvalidContactsForGroup(invalidRows.map((row) => toImportedContactModelWithErrors(row)));
                    }}
                />;
            case 3:
                return <ButtonComponent ButtonLabel={`Import ${existingContacts?.length + newValidContacts?.length} Contacts`} Variant={'primary-btn'} OnClick={() => handleImport()} Loading={isImportLoading} />
        }
    }

    useEffect(() => {
        if (CustomAttributeDefinitions?.length <= 0) {
            LoadCustomAttributeDefinitions(dispatch);
        }
    }, []);

    useEffect(() => {
        const scrollContainer = stepContentScrollRef.current;
        if (!scrollContainer) return;

        scrollContainer.scrollTop = 0;
        requestAnimationFrame(() => {
            scrollContainer.scrollTop = 0;
        });
    }, [activeStep]);


    return (
        <div className={`w-p-98 mx-auto h-p-98 ${IsDarkMode ? "bg-color-primary-background" : "bg-color-report-background"} border-primary-divider`}>
            <div className='w-p-85 h-p-100 mx-auto'>
                <div className='color-primary-light h6-semibold w-p-100 h-p-18 d-flex  flex-direction justify-center'>
                    <div>STEP {activeStep + 1} OF {Steps?.length} </div>
                    <div className='h1-medium color-primary-accent'>{StepHeader[activeStep]?.Title}</div>
                    <div className='w-p-100 d-flex justify-between color-primary-accent align-center'>
                        <div className='h4-regular'>{StepHeader[activeStep]?.Description}</div>
                        {!!uploadedFile && <div className='d-flex align-center border-primary-divider px-1 br-2 w-p-30 justify-between bg-color-secondary'>
                            <div className='d-flex align-center'>
                                <div className='bg-color-secure-file-active d-flex align-center p-1 br-2'>
                                    <MediaDefaultIcon className='w-2 h-2' />
                                </div>
                                <div className='d-flex flex-direction justify-center ml-1'>
                                    <div className='h5-medium w-break'>{uploadedFile?.name}</div>
                                    <div className='h5-regular color-menu-list-font-subnav'><span className='mr-1'>{formatFileSize(uploadedFile?.size)}</span>  <span>{jsonFileData?.length} rows</span></div>
                                </div>
                            </div>
                            <div className='h5-medium color-menu-list-font-subnav t-decoration-underline c-pointer' onClick={handleChangeFile}>Remove</div>
                        </div>
                        }
                    </div>
                </div>
                <div className='d-flex h-p-10 align-center w-p-100'>
                    <div className={`w-p-100 mx-auto br-5 h-p-75 bg-color-secondary border-primary-divider`}>
                        <Stepper activeStep={activeStep} className={'c-pointer my-auto d-flex align-center h-p-100 w-p-95 mx-auto'}
                            sx={{
                                "& .MuiStepIcon-root.Mui-completed": {
                                    color: "success.main"
                                },

                                // default line
                                "& .MuiStepConnector-line": {
                                    borderTopWidth: 4,
                                    borderRadius: 10,
                                    borderTopColor: "#d3d3d3"
                                },

                                // completed line
                                "& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": {
                                    borderTopColor: "var(--color-primary-success)"
                                },

                                // active line
                                "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line": {
                                    borderTopColor: "var(--color-primary-success)"
                                }
                            }}
                        >
                            {Steps.map((step, index) => (
                                <Step key={index}  >
                                    <StepLabel className={'h3-regular color-primary-accent'} StepIconProps={{
                                        sx: {
                                            "&.Mui-completed": {
                                                color: "green"
                                            }
                                        }
                                    }}
                                    >{step.label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </div>
                </div>
                <div ref={stepContentScrollRef} className='d-flex w-p-90 mx-auto h-p-72 flex-direction o-y-scroll '>
                    {Steps[activeStep]?.component}
                    <div className='d-flex justify-between w-p-96 mx-auto'>
                        <ButtonComponent ButtonLabel={'Back'} Variant={activeStep === 0 ? 'disabled-btn' : 'primary-btn'} OnClick={() => setActiveStep(activeStep - 1)} />
                        {renderNextStepButton(activeStep)}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default StepperImportNewComponent